import { formatMarkdown } from "@/components/formatMarkdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getProviderMeta } from "@/lib/provider/meta";
import { nonFalse } from "@/lib/utils";
import { ProviderTypes } from "@/types";
import { Server, Bot, Rocket, ServerCog, Archive, BrainCircuit } from "lucide-react";

interface FeatureProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const featuresData: FeatureProps[] = [
  {
    title: "DollarDeploy AI",
    description:
      "Deploy apps, provision new servers, and troubleshoot production - just by chatting. Our built-in AI agent understands your infrastructure and takes real action on your behalf. [Learn more.](https://docs.dollardeploy.com/ai/)",
    icon: <Bot className="h-8 w-8 text-primary" />
  },
  {
    title: "Next.js, Go, Java and more",
    description:
      "We build your app on our infra and deploy to your server. Your apps runs natively as a systemd service without the overhead or complexity of Docker. [Learn more.](https://docs.dollardeploy.com/#supported-app-types)",
    icon: <Rocket className="h-8 w-8 text-primary" />
  },
  {
    title: "Integrated provisioning",
    description: `Create new servers using our integration with ${ProviderTypes.map(
      getProviderMeta
    )
      .filter(nonFalse)
      .filter(p => !p.experimental && p.compute)
      .map(p => p.name)
      .join(", ")}, and we will manage it via SSH. Full control, no vendor lock-in.`,
    icon: <Server className="h-8 w-8 text-primary" />
  },
  {
    title: "Linux, properly configured",
    description:
      "We configure and secure Nginx reverse proxy with automatic HTTPS via Let's Encrypt, with best practices and settings so your apps work with great performance.",
    icon: <ServerCog className="h-8 w-8 text-primary" />
  },
  {
    title: "AI & LLMs support",
    description:
      "Thanks to our provider Verda (formerly DataCrunch), you can deploy LLMs such as [DeepSeek](https://dollardeploy.com/r/local-ai-nvidia-cuda-12) or [GPT-OSS](https://dollardeploy.com/r/gpt-oss) in one click.",
    icon: <BrainCircuit className="h-8 w-8 text-primary" />
  },
  {
    title: "One-click backups",
    description:
      "Backup to S3 compatible storage your database and all applications. We also backup docker volumes and all your application files.",
    icon: <Archive className="h-8 w-8 text-primary" />
  }
];

export const Features = () => {
  return (
    <section id="features" className="container space-y-8 md:space-y-16">
      <h2 className="text-center text-3xl lg:text-4xl font-bold md:text-center">
        AI-powered deployment,{" "}
        <span className="gradient-light dark:gradient-dark">
          and everything to run your apps
        </span>
        .
      </h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {featuresData.map(({ title, description, icon }: FeatureProps) => (
          <Card key={title} className="bg-muted/50 border">
            <CardHeader className="flex flex-row items-center gap-4 pt-2">
              {icon}
              <CardTitle className="text-xl font-bold">{title}</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              {formatMarkdown(description)}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};
